/**
 * ChatEdgeHandle — the desktop chat opener: a narrow tab on the content column's
 * right edge, where the dock slides out.
 *
 * It replaces a button that floated over the top-right of the page, which made
 * every row sharing its top edge reserve room for it. On the edge it covers no
 * page's controls. Anchored to the content column, it rides the dock's left
 * edge when the dock is open, so one control both opens and closes it.
 *
 * It is a 20px tab that always shows the assistant's Sparkles icon, the same
 * glyph as the phone toggle and the Chat destination, so the control is
 * visible without hovering. Hover or keyboard focus lifts it to the brand
 * colour. The button is 8px wider than the paint so the narrow tab is not a
 * fiddly target. Desktop only — the phone keeps `GlobalChatToggle` in its top
 * row.
 *
 * Design: docs/plans/2026-10-17-hide-tab-strip-design.md §3.2.
 */
import React from 'react';

import { Icons } from '@shared/components/ui';
import { Z } from '@shared/constants/zIndex';

import { useChatToggle } from './useChatToggle';

export const ChatEdgeHandle: React.FC = () => {
  const { visible, isOpen, toggle } = useChatToggle();
  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isOpen ? 'Close assistant' : 'Open assistant'}
      aria-pressed={isOpen}
      title="Assistant"
      data-testid="chat-edge-handle"
      className={`group absolute top-1/2 right-0 hidden h-12 w-7 -translate-y-1/2 items-center justify-end focus-visible:outline-none nav:flex`}
      style={{ zIndex: Z.CHAT_TOGGLE }}
    >
      <span
        className={`border-border flex h-12 w-5 items-center justify-center rounded-l-md border border-r-0 transition-[background-color,color] duration-150 group-hover:bg-card group-hover:text-brand group-focus-visible:bg-card group-focus-visible:text-brand ${
          isOpen ? 'bg-card text-brand' : 'bg-sidebar text-muted-foreground'
        }`}
      >
        <Icons.Sparkles size="xs" color="inherit" />
      </span>
    </button>
  );
};
